import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TypeExam, TypeExamAPI, TypeExamStatus, TypeSubject } from 'app/constants/types/exam.types';
import { listExams } from 'aws/dynamodb';
import { getEnrolledExams } from 'aws/lambda';
import dayjs from 'dayjs';
import { secondsToDHms } from 'app/hooks/useCountdown';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FULL_DATE_FORMAT } from 'app/constants/content';
import { capitalizeFirstLetter } from 'app/utils';
import { mapExamStatus } from 'app/components/ExamStatus/utils/mapExamStatus';

// Define types for enrolled exams data
export interface EnrolledExam {
  userId: string;
  examId: string;
  createdAt: number;
  updatedAt: number;
}

export interface EnrolledExamsResponse {
  userId: string;
  enrolledExams: EnrolledExam[];
  enrolledHLQuantity: number;
  enrolledSLQuantity: number;
}

export interface ExamState {
  exams: TypeExam[];
  enrolledExams: EnrolledExam[];
  enrolledExamsLoading: boolean;
  enrolledExamsError: string | null;
  loading: boolean;
  error: string | null;
  isSynchronizing: boolean;  // True when both fetches are in progress
  lastSyncTime: number | null; // Timestamp of last successful sync
}

export const initialState: ExamState = {
  exams: [],
  enrolledExams: [],
  enrolledExamsLoading: false,
  enrolledExamsError: null,
  loading: false,
  error: null,
  isSynchronizing: false,
  lastSyncTime: null,
};

const getExamStatus = ({
  examTime,    // exam start time in unix timestamp
  examDuration, // in minutes
  status,
  submissionStatus  // new parameter to check submission
}: TypeExamAPI & { submissionStatus?: { submitted: boolean } }): TypeExamStatus => {

  const now = Date.now();
  const endedTime = examTime + (examDuration * 60 * 1000); // convert minutes to milliseconds
  const oneHourAfterEnd = endedTime + (60 * 60 * 1000); // 1 hour after exam end

  if (status == "standby-locked") {

    return "standby-locked"

  }

  if (status == "standby-open") {

    return "standby-open"

  }

  if (status === "results-released") {

    return "graded";

  }

  if (status === "ongoing") {

    return "ongoing";

  }

  if (status === "submitted") {

    return "submitted";

  }

  if (status === "ended") {

    if (now <= oneHourAfterEnd && !submissionStatus?.submitted) {

      return "missing"; // Only show missing if within 1 hour AND no submission

    }

    return "ended"; // Show ended for all other cases, including if submitted

  }

  if (now >= examTime && now <= endedTime) {

    return "ongoing";

  }

  else {

    if (now <= oneHourAfterEnd && !submissionStatus?.submitted) {

      return "missing"; // Only show missing if within 1 hour AND no submission

    }

    return "ended";

  }

};

export const refactorExamList = async (examList: TypeExamAPI[]): Promise<TypeExam[]> => {
  const examsWithSubmissions = examList.map((item: TypeExamAPI) => {

    const submissionStatus = { submitted: item.status === 'submitted' };

    return {
      ...item,
      description: capitalizeFirstLetter(item.description),
      examTime: dayjs(item.examTime).format(FULL_DATE_FORMAT),
      examDuration: secondsToDHms((item.examDuration || 0) * 60),
      originalStatus: item.status,
      status: getExamStatus({ ...item, submissionStatus }),
      rawData: {
        examTime: item.examTime,
        examDuration: item.examDuration,
        status: item.status,
      }
    };
  });
  return examsWithSubmissions;
};

type TypePostFunction = (exams: TypeExamAPI[]) => TypeExam[];

export const fetchEnrolledExams = createAsyncThunk(
  'exams/fetchEnrolledExams',
  async () => {
    try {
      const response = await getEnrolledExams();
      console.log('Enrolled exams response:', response);

      return response as EnrolledExamsResponse;
    } catch (error) {
      console.error('Error fetching enrolled exams:', error);
      throw error;
    }
  }
);

// Fetch exams with enrollment status
export const fetchExams = createAsyncThunk(
  'exams/fetchExams',
  async (postFunction: TypePostFunction | undefined, thunkAPI) => {
    try {
      // Get current state
      const state = thunkAPI.getState() as { examStore: ExamState };
      const { enrolledExams, enrolledExamsLoading } = state.examStore;

      // If enrolled exams are still loading, wait a bit and check again
      // This helps ensure we have enrollment data before mapping statuses
      if (enrolledExamsLoading || enrolledExams.length === 0) {
        console.log('Waiting for enrolled exams data to be available...');
        // Wait for a short time to allow enrolled exams to load
        await new Promise(resolve => setTimeout(resolve, 500));

        // Get state again after waiting
        const updatedState = thunkAPI.getState() as { examStore: ExamState };
        const updatedEnrolledExams = updatedState.examStore.enrolledExams;

        // If still no enrolled exams after waiting, dispatch the enrolled exams action
        if (updatedEnrolledExams.length === 0) {
          console.log('No enrolled exams found after waiting, fetching them now');
          try {
            // Dispatch and wait for it to complete
            await thunkAPI.dispatch(fetchEnrolledExams());
            // Wait a bit more for the state to update
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (error) {
            console.error('Failed to fetch enrolled exams:', error);
            // Continue anyway - we'll use empty enrollment data
          }
        }
      }

      // Fetch exams data
      const data: TypeExamAPI[] = await listExams();
      let result = await refactorExamList(data);

      // Get final state with most up-to-date enrolled exams
      const finalState = thunkAPI.getState() as { examStore: ExamState };
      const finalEnrolledExams = finalState.examStore.enrolledExams;

      console.log('Mapping exam statuses with enrolled exams:', finalEnrolledExams.length);

      // Map exams with enrollment status
      result = result.map(exam => {
        // Check if this exam is in the enrolled exams list
        const isEnrolled = finalEnrolledExams.some(
          enrolledExam => enrolledExam.examId === exam.examId
        );

        // Get submission status
        const hasSubmission = exam.status === 'submitted';

        // Map the status using our utility function
        const mappedStatus = mapExamStatus(
          exam.rawData.status,
          isEnrolled,
          exam.examTime,
          exam.examDuration,
          hasSubmission
        );

        return {
          ...exam,
          status: mappedStatus,
          originalStatus: exam.status, // Keep original status for reference
        };
      });

      if (postFunction) {
        result = postFunction(data);
      }

      return result;
    } catch (error) {
      console.error('Error in fetchExams:', error);
      throw error;
    }
  }
);

// Create synchronized fetch action
export const synchronizeFetchExams = createAsyncThunk(
  'exams/synchronizeFetchExams',
  async (_, thunkAPI) => {
    try {
      // Mark synchronization as started
      thunkAPI.dispatch(setSynchronizing(true));

      // Start both fetches in parallel - use allSettled to handle failures gracefully
      const enrolledExamsPromise = thunkAPI.dispatch(fetchEnrolledExams());
      const examsPromise = listExams(); // Direct API call

      // Wait for both to complete using allSettled to prevent one failure from blocking the other
      const results = await Promise.allSettled([
        enrolledExamsPromise,
        examsPromise
      ]);

      // Extract results
      const enrolledExamsResult = results[0].status === 'fulfilled' ? results[0].value : null;
      const examsData = results[1].status === 'fulfilled' ? results[1].value : null;

      // If exams data failed to fetch, we can't continue
      if (!examsData) {
        throw new Error('Failed to fetch exams data from DynamoDB');
      }

      // Handle enrolled exams result gracefully
      let enrolledExams: EnrolledExam[] = [];
      if (!enrolledExamsResult || enrolledExamsResult.type === fetchEnrolledExams.rejected.type) {
        console.warn('Failed to fetch enrolled exams, continuing with empty enrollment data');
        // Don't throw - continue with empty enrolled exams array
        enrolledExams = [];
      } else {
        // Get the enrolled exams from the result
        enrolledExams = (enrolledExamsResult.payload as EnrolledExamsResponse)?.enrolledExams || [];
      }

      console.log('Processing exams with enrollment data:', enrolledExams.length, 'enrolled exams');

      // Process the exams with submission status
      const processedExams = await refactorExamList(examsData);

      // Map the exams with enrollment status
      const result = processedExams.map(exam => {
        // Check if this exam is in the enrolled exams list
        const isEnrolled = enrolledExams.some(
          enrolledExam => enrolledExam.examId === exam.examId
        );

        // Get submission status
        const hasSubmission = exam.status === 'submitted';

        // Map the status using our utility function
        const mappedStatus = mapExamStatus(
          exam.rawData.status,
          isEnrolled,
          exam.examTime,
          exam.examDuration,
          hasSubmission
        );

        return {
          ...exam,
          status: mappedStatus,
          originalStatus: exam.status,
        };
      });

      // Update last sync time
      thunkAPI.dispatch(setLastSyncTime(Date.now()));

      return result;
    } catch (error) {
      console.error('Error in synchronizeFetchExams:', error);
      throw error;
    } finally {
      // Always mark synchronization as complete
      thunkAPI.dispatch(setSynchronizing(false));
    }
  }
);

const slice = createSlice({
  name: 'examStore',
  initialState: initialState,
  reducers: {
    setExams(state, action) {
      state.exams = action.payload.Items;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setSynchronizing(state, action) {
      state.isSynchronizing = action.payload;
    },
    setLastSyncTime(state, action) {
      state.lastSyncTime = action.payload;
    },
    addEnrolledExam(state, action) {
      const newEnrollment: EnrolledExam = {
        userId: action.payload.userId,
        examId: action.payload.examId,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      // Check if already enrolled to avoid duplicates
      const alreadyEnrolled = state.enrolledExams.some(
        exam => exam.examId === newEnrollment.examId
      );

      if (!alreadyEnrolled) {
        state.enrolledExams.push(newEnrollment);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle synchronizeFetchExams
      .addCase(synchronizeFetchExams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(synchronizeFetchExams.fulfilled, (state, action) => {
        state.exams = action.payload;
        state.loading = false;
      })
      .addCase(synchronizeFetchExams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to synchronize exams';
      })

      // Handle fetchExams
      .addCase(fetchExams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExams.fulfilled, (state, action) => {
        state.exams = action.payload;
        state.loading = false;
      })
      .addCase(fetchExams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch exams';
      })

      // Handle fetchEnrolledExams
      .addCase(fetchEnrolledExams.pending, (state) => {
        state.enrolledExamsLoading = true;
        state.enrolledExamsError = null;
      })
      .addCase(fetchEnrolledExams.fulfilled, (state, action) => {
        if (action.payload && action.payload.enrolledExams) {
          state.enrolledExams = action.payload.enrolledExams;
        } else {
          state.enrolledExams = [];
        }
        state.enrolledExamsLoading = false;
      })
      .addCase(fetchEnrolledExams.rejected, (state, action) => {
        state.enrolledExamsLoading = false;
        state.enrolledExamsError = action.error.message || 'Failed to fetch enrolled exams';
      });
  },
});

export const {
  setExams,
  setLoading,
  setError,
  setSynchronizing,
  setLastSyncTime,
  addEnrolledExam
} = slice.actions;
export default slice.reducer;
export const { actions: examsActions } = slice;

export const useExamStoreSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  return { actions: slice.actions };
};
